import React from 'react';
import { cn } from '../../../utils/cn';

/**
 * Card Component
 * 
 * Container component for grouping related content
 * 
 * @param {boolean} bordered - Show border
 * @param {boolean} elevated - Show shadow elevation
 * @param {boolean} hoverable - Add hover effect
 * @param {boolean} clickable - Add click cursor and scale effect
 * @param {string} padding - Padding size: 'none' | 'sm' | 'md' | 'lg'
 * @param {Function} onClick - Click handler
 */
export const Card = ({
  bordered = true,
  elevated = false,
  hoverable = false,
  clickable = false,
  padding = 'md',
  className,
  children,
  onClick,
  ...props
}) => {
  const paddingStyles = {
    none: 'p-0',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-white rounded-lg',
        'transition-all duration-200',
        
        // Border
        bordered && 'border border-slate-200',
        
        // Shadow/Elevation
        elevated && 'shadow-sm',
        !elevated && !bordered && 'shadow-none',
        
        // Hover effects
        hoverable && 'hover:shadow-md',
        
        // Clickable
        (clickable || onClick) && 'cursor-pointer hover:shadow-md active:scale-[0.99]',
        
        // Padding
        paddingStyles[padding],
        
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * CardHeader Component
 * 
 * Card header with title, description, and actions
 */
export const CardHeader = ({
  title,
  description,
  action,
  bordered = false,
  className,
  children,
  ...props
}) => {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4',
        bordered && 'border-b border-slate-200 pb-4',
        className
      )}
      {...props}
    >
      <div className="flex-1 min-w-0">
        {title && (
          <h3 className="text-lg font-semibold text-slate-900 truncate">
            {title}
          </h3>
        )}
        {description && (
          <p className="mt-1 text-sm text-slate-500">
            {description}
          </p>
        )}
        {children}
      </div>
      {action && (
        <div className="flex-shrink-0">
          {action}
        </div>
      )}
    </div>
  );
};

/**
 * CardTitle Component
 * 
 * Standalone card title
 */
export const CardTitle = ({
  as: Component = 'h3',
  className,
  children,
  ...props
}) => {
  return (
    <Component
      className={cn(
        'text-lg font-semibold text-slate-900',
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

/**
 * CardDescription Component
 * 
 * Standalone card description
 */
export const CardDescription = ({
  className,
  children,
  ...props
}) => {
  return (
    <p
      className={cn(
        'text-sm text-slate-500',
        className
      )}
      {...props}
    >
      {children}
    </p>
  );
};

/**
 * CardBody Component
 * 
 * Main content area of card
 */
export const CardBody = ({
  className,
  children,
  ...props
}) => {
  return (
    <div
      className={cn('space-y-4', className)}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * CardFooter Component
 * 
 * Card footer with actions
 */
export const CardFooter = ({
  bordered = false,
  className,
  children,
  ...props
}) => {
  return (
    <div
      className={cn(
        'flex items-center gap-2',
        bordered && 'border-t border-slate-200 pt-4',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * CardImage Component
 * 
 * Image for card (usually at top)
 */
export const CardImage = ({
  src,
  alt = '',
  aspectRatio = 'video', // 'video' (16:9), 'square', 'portrait', 'auto'
  className,
  ...props
}) => {
  const aspectRatioClasses = {
    video: 'aspect-video',
    square: 'aspect-square',
    portrait: 'aspect-[3/4]',
    auto: '',
  };

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-t-lg',
        aspectRatioClasses[aspectRatio],
        className
      )}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        {...props}
      />
    </div>
  );
};

/**
 * CardGrid Component
 * 
 * Grid layout for multiple cards
 */
export const CardGrid = ({
  cols = 3,
  gap = 6,
  className,
  children,
  ...props
}) => {
  const colsClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  };

  const gapClasses = {
    2: 'gap-2',
    4: 'gap-4',
    6: 'gap-6',
    8: 'gap-8',
  };

  return (
    <div
      className={cn(
        'grid',
        colsClasses[cols],
        gapClasses[gap],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export default Card;